import { useMemo } from 'react';
import { Pencil, ShoppingBag, Trash2 } from 'lucide-react';
import EmptyStateCard from '../../components/EmptyStateCard';

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

export default function MerchantProductsView({
  products,
  deletingProductId,
  onOpenAddProduct,
  onEditProduct,
  onRequestDelete,
}) {
  // Trois chiffres que le marchand n'avait nulle part : combien de références,
  // ce que le stock représente en argent, et ce qui est tombé en rupture.
  const stats = useMemo(() => {
    let valeur = 0;
    let ruptures = 0;
    products.forEach((product) => {
      const stock = Number(product.stock) || 0;
      valeur += stock * (Number(product.price) || 0);
      if (product.stock === 0) ruptures += 1;
    });
    return { total: products.length, valeur, ruptures };
  }, [products]);

  if (products.length === 0) {
    return (
      <EmptyStateCard
        icon={ShoppingBag}
        tone="info"
        title="Aucun produit"
        description={'Ajoute tes produits pour\ncréer des commandes rapidement.'}
        actionLabel="Ajouter un produit"
        onAction={onOpenAddProduct}
      />
    );
  }

  return (
    <>
      {/* Même ouverture que les onglets Commandes et Bilan. Pas d'illustration
          ici : aucune ne parle de stock, et une tuile sans image tient très
          bien — voir « CA à risque » sur Retours. */}
      <div className="stat-tiles stat-tiles--3">
        {[
          { label: 'Produits',      value: stats.total,         accent: '#173b78' },
          { label: 'Valeur stock',  value: `${fmt(stats.valeur)} F`, accent: '#16a34a', money: true },
          { label: 'En rupture',    alert: stats.ruptures > 0, value: stats.ruptures,      accent: stats.ruptures > 0 ? '#c53a2d' : '#94a3b8' },
        ].map((tile) => (
          <div key={tile.label} className="stat-tile stat-tile--static">
            <span className={`stat-tile-bar${tile.alert ? ' stat-tile-bar--alert' : ''}`} />
            <span className="stat-tile-text">
              <span
                className={`stat-tile-value${tile.money ? ' stat-tile-value--money' : ''}`}
                style={{ color: tile.accent }}
              >
                {tile.value}
              </span>
              <span className="stat-tile-label">{tile.label}</span>
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {products.map((product) => (
          <div
            key={product.id}
            style={{ background: 'white', borderRadius: '14px', border: '1px solid #eef0f6', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.15s' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(19,40,72,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
            }}
          >
            {/* Pas de pastille à icône : elle était identique sur chaque ligne
                et ne distinguait rien. Le nom mène la ligne. */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a', marginBottom: '4px' }}>
                {product.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#173b78' }}>
                  {fmt(product.price)} FCFA
                </div>
                {product.stock !== null && product.stock !== undefined && (
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '6px',
                      background: product.stock === 0 ? '#fff1f2' : '#f0fdf4',
                      color: product.stock === 0 ? '#be123c' : '#15803d',
                      border: `1px solid ${product.stock === 0 ? '#fecdd3' : '#bbf7d0'}`,
                    }}
                  >
                    {product.stock === 0 ? 'Rupture' : `${product.stock} en stock`}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => onEditProduct(product)}
              style={{ width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #c7d7ef', background: '#eef4ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#dbe7f7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#eef4ff';
              }}
            >
              <Pencil size={14} color="#173b78" />
            </button>
            <button
              onClick={() => onRequestDelete(product)}
              disabled={deletingProductId === product.id}
              style={{ width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #fecdd3', background: '#fff1f2', cursor: deletingProductId === product.id ? 'not-allowed' : 'pointer', opacity: deletingProductId === product.id ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}
              onMouseEnter={(e) => {
                if (deletingProductId !== product.id) e.currentTarget.style.background = '#fecdd3';
              }}
              onMouseLeave={(e) => {
                if (deletingProductId !== product.id) e.currentTarget.style.background = '#fff1f2';
              }}
            >
              <Trash2 size={14} color="#be123c" />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
